<template>
    <div class="assignment-step" v-if="canViewStep">

        <div class="page-content">

            <div class="note note--warning note--large" v-if="shouldRetake && !isInspectingStudent">
                <h2><span class="icon icon--inline icon--retake-dark"></span> Opnieuw inleveren</h2>
                <p>
                Je moet deze opdracht opnieuw inleveren door de stappen nog een keer te doorlopen. Je hiervoor ingeleverde werk blijft zichtbaar.
                <strong>Let goed op de feedback die je hebt gekregen!</strong>
                </p>
                <primary-button :link="{ name: 'assignment.step', params: { stepIdx: 1 } }" class="primary-button--no-margin">Begin opdracht</primary-button>
            </div>

            <div class="note note--info note--large" v-if="hasDiligence">
              <diligence :progress="progress" :readonly="true"></diligence>
            </div>


            <h1 class="banner">
                <span v-if="!isInspectingStudent">Dit heb jij gemaakt</span>
                <span v-else>Dit heeft {{ user.firstname }} gemaakt</span>
            </h1>

            <component v-for="partial in regularInputPartials"
                :is="partial.type"
                :submissions="partial.submissions"
                :title="partial.title"
                :showVersions="true"
                :key="partial.id"></component>


            <template v-if="isInspectingStudent && reflectingInputPartials.length">
              <h1 class="banner">
                  <span>Feedback van {{ user.firstname}}</span>
              </h1>

              <component v-for="partial in reflectingInputPartials"
                  :is="partial.type"
                  :submissions="partial.submissions"
                  :title="partial.title"
                  :showVersions="true"
                  :key="partial.id"></component>

              <a href="#" v-if="reflectionPartials.length" @click.prevent="showReflection(reflectionPartials[0], progress)">Bekijk het werk waar {{ user.firstname }} feedback op heeft gegeven</a>
            </template>

            <h1 v-if="reflections || hasFeedback" class="banner">
                <span v-if="!isInspectingStudent">Feedback</span>
                <span v-else>Feedback voor {{ user.firstname }}</span>
            </h1>

            <div v-for="reflection in reflections" class="reflection">
                <h1><small>Van:</small> {{ userForReflection(reflection).firstname }}</h1>
                <h2>{{ title(partialForReflection(reflection)) }}</h2>
                <p class="preformatted">{{ reflection.data }}</p>
            </div>

            <teacher-feedback v-if="hasFeedback" :feedback="feedback"></teacher-feedback>

            <group-submissions :assignment="assignment" :group="group" v-if="!isInspectingStudent"></group-submissions>

        </div>

        <nav class="action-nav">
            <hr class="flipped">
            <router-link :to="{ name: 'assignment.step', params: { stepIdx: assignment.steps.length }}" class="tag button-timid button-backward">Vorige stap</router-link>
            <primary-button :link="returnLink.route" class="action-nav__next" v-if="!isInspectingStudent">Klaar</primary-button>
        </nav>
    </div>
</template>


<script>
import step from './stepMixin'

import { transform } from 'js/vuex/helpers';
import api from 'js/api/resources'
import track from 'js/helpers/analytics';
import { canTeach } from 'js/api/auth';

import GroupSubmissions from './presenters/GroupSubmissions.vue'
import TeacherFeedback from './presenters/TeacherFeedback.vue'
import Diligence from 'js/components/assignment/Diligence.vue';
import Question from './presenters/Question.vue';
import Upload from './presenters/Upload.vue';

export default {
    name: 'Overview',
    mixins: [ step ],
    props: {
        group: {},
        isInspectingStudent: {},
        isReviewing: {},
        user: {},
        returnLink: {
            type: Object,
            required: true
        }
    },
    components: { Question, Upload, GroupSubmissions, TeacherFeedback, Diligence },

    data: function() {
        return {
            reflections: null,
            users: null,
        }
    },

    vuex: {
        actions: {

            reviewedProgress: ({dispatch}, progress) => {
                return api.reviewedProgress(progress.id)
                    .then( response => transform(response.body) )
                    .then( result => dispatch('ADD_PROGRESS', result.progress) )
            },

            showReflection: ({dispatch}, partial, progress) => {
                dispatch('SHOW_MODAL', 'ReflectionPartial', { partial, progress })
            },

            showBadge: ({dispatch}, badge) => {
              dispatch('SHOW_ACHIEVEMENT', badge.type, 'red')
            }

        }
    },

    computed: {

        allPartials: function() {
            return this.steps.reduce((result, step) => {
                const partials = step.partials
                  .map( id => this.$store.state.partials.storage[id] )
                  .map( data => ({ ...data, isReflection: step.type === 'reflection' }) )

                return result.concat( partials )
            }, [])
        },

        allInputPartials: function() {
            return this.allPartials
                .filter(partial => this.isInput(partial.type))
                .map( p => {

                    const submissions = this.submissionsForPartial(p.id)

                    return {
                        id: p.id,
                        type: submissions.length ? this.typeOfSubmission(submissions[0]) : p.type,
                        submissions: submissions,
                        title: this.title(p),
                        isReflection: p.isReflection
                    }

                })
        },

        regularInputPartials: function() {
            return this.allInputPartials.filter(p => !p.isReflection)
        },

        // all input partials reflecting on work other student
        reflectingInputPartials: function() {
            return this.allInputPartials.filter(p => p.isReflection)
        },

        // all partials that show other student work
        reflectionPartials: function() {
            return this.allPartials.filter(partial => partial.type === 'Reflection')
        },

        feedback: function() {

            if ( this.progress.feedback == null || this.progress.feedback.length === 0 ) {
                return null
            }

            return this.$store.state.feedback.storage[this.progress.feedback[0]]
        },

        hasFeedback: function() {
            if ( this.feedback == null ) {
                return false
            }

            // at least one must be set
            return this.feedback.content || this.feedback.remarks.length || this.feedback.compliments.length
        },

        shouldRetake: function() {
            return this.progress.isRetaking
        },

        hasDiligence: function() {
            return this.progress.diligence != null && this.progress.diligence.length > 0
        },

        hasBadge: function() {
            return this.progress.badge != null && this.progress.badge.length > 0
        },

        badge: function () {
          if (!this.hasBadge) {
            return null
          }
            return this.$store.state.badges.storage[ this.progress.badge[0] ]
        },

        isTeacher: function() {
            return canTeach(this.$store.state.users.current)
        },

        canViewStep: function() {
            return this.isProgressFinished(this.progress)
        },

        submissions: function() {
            return Object.keys(this.$store.state.submissions.storage)
                .map( id => this.$store.state.submissions.storage[id] )
                .filter( submission => submission.progress === this.progress.id )
        },

    },
    methods: {
        partialForReflection: function(reflection) {
            return this.$store.state.partials.storage[reflection.partial]
        },
        userForReflection: function(reflection) {
            return this.users.find( user => user.id == reflection.user[0])
        },
        isInput: function(type) {
            // TODO: replace arr with this.components?
            const inputs = ['Question', 'Upload'];
            return inputs.indexOf(type) !== -1;
        },
        submissionsForPartial: function(partialId) {
            return this.submissions
                .filter( submission => submission.partial === partialId )
        },
        typeOfSubmission: function(submission) {
            return (submission.data instanceof Object && submission.data.medium != null && submission.data.thumb != null)
                ? 'Upload'
                : 'Question'
        },
        title: function(partial) {
            if ( 'title' in partial.data ) {
                return partial.data.title
            }

            return ''

        },
        checkRoute: function() {

            if ( ! this.canViewStep ) {
                this.autoRedirectStep(this.progress)
                return false
            }

            return true

        },
    },
    created: function() {
        if ( !this.checkRoute() ) {
            return
        }

        if (!this.isInspectingStudent && this.hasBadge) {
          setTimeout(() => {
            console.log('show', this.badge)
            this.showBadge(this.badge)
          }, 1000)
        }

        api.getReflections(this.progress.id)
            .then( response => transform(response.body) )
            .then( result => {
                this.reflections = result.submissions
                this.users = result.users
            })
            .then( () => {
                if ( this.isReviewing && !this.progress.isReviewed ) {
                    this.reviewedProgress(this.progress)
                        .catch( err => track.exception('Can\'t mark progress reviewed', err))
                }
            })
            .catch( err => {
                track.exception('Can\'t load reflection ', err)
            })
    }
}
</script>


<style lang="scss">

    @import 'global';


    .reflection {
        margin: 3em 0;
    }

</style>



// WEBPACK FOOTER //
// StepOverview.vue?130f368e